package com.essntl.features.proposal.ui.proposalhistory.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import com.essntl.features.proposal.domain.model.ProposalModel
import com.essntl.features.proposal.ui.proposalhistory.ProposalOverviewState
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsLargeIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.date.EsDateInput
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.rememberContains
import com.essntl.uikit.rememberFind
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun ProposalBaseInformation(
    state: ProposalOverviewState,
    proposal: ProposalModel,
) {
    var isExpanded by rememberSaveable { mutableStateOf(false) }

    val request = rememberFind(
        state.getAllRequest.data.orEmpty(),
        proposal.requestId,
    ) { request -> request.id }

    val proposalAreas = rememberContains(
        state.getAllArea.data.orEmpty(),
        proposal.areas.orEmpty().toSet(),
    ) { area -> area.id }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(
                color = MaterialTheme.esColorScheme.bgSecondary,
                shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
            )
            .padding(
                vertical = 2.spacing,
                horizontal = 2.5f.spacing,
            ),
    ) {
        Row(
            horizontalArrangement = Arrangement.SpaceBetween,
            modifier = Modifier
                .fillMaxWidth(),
        ) {
            Text(
                text = "Base Information",
                color = MaterialTheme.esColorScheme.textPrimary,
                style = MaterialTheme.esTypography.s2SemiBold16,
            )

            EsLargeIcon(
                res = EsIcons.ArrowDown,
                modifier = Modifier
                    .graphicsLayer {
                        transformOrigin = TransformOrigin.Center
                        rotationZ = if (isExpanded) 180f else 0f
                    }
                    .clip(CircleShape)
                    .pointerHoverIcon(PointerIcon.Hand)
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                    ) {
                        isExpanded = !isExpanded
                    },
            )
        }

        AnimatedVisibility(isExpanded) {
            Column(
                modifier = Modifier
                    .fillMaxWidth(),
            ) {
                Spacer(modifier = Modifier.height(3.spacing))

                EsInput(
                    value = proposal.title ?: "",
                    onValueChange = {},
                    label = "Title",
                    enabled = false,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = proposal.headline ?: "",
                    onValueChange = {},
                    label = "Internal notes",
                    enabled = false,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                EsInput(
                    value = proposalAreas.map { it.name ?: "" }.filter { it.isNotEmpty() }.joinToString(", ")
                        .ifEmpty { "Unknown destination" },
                    onValueChange = {},
                    label = "Area",
                    enabled = false,
                )

                Spacer(modifier = Modifier.height(2.spacing))

                Row {
                    EsDateInput(
                        value = proposal.fromDate.orEmpty(),
                        onValueChange = {},
                        label = "From",
                        enabled = false,
                        modifier = Modifier
                            .weight(1f),
                    )

                    Spacer(modifier = Modifier.width(1.5f.spacing))

                    EsDateInput(
                        value = proposal.toDate.orEmpty(),
                        onValueChange = {},
                        label = "To",
                        enabled = false,
                        modifier = Modifier
                            .weight(1f),
                    )
                }
            }
        }
    }
}